// src/hooks/useSignalRNotifications.ts
// Centralised user-facing SignalR connection for the EM /notifications hub.
// Mount once in HeaderBar (or DashboardLayout).  Dispatches into the existing
// Redux notificationSlice so every component automatically gets live updates.

import { useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../redux/store';
import {
  prependNotification,
  fetchUnreadCount,
  fetchNotifications,
  markOneReadLocal,
  markAllReadLocal,
} from '../redux/slices/notificationSlice';
import { toast } from 'react-hot-toast';

/**
 * Establishes a persistent SignalR connection to EM /notifications.
 *
 * Events handled:
 *  - ReceiveNotification(message: string) → toast + prepend + bump unread
 *  - NotificationRead(notificationId: number) → mark single item read locally
 *  - AllNotificationsRead(userId: number) → mark all items read locally
 *
 * The connection is authenticated via the JWT in localStorage and uses
 * automatic reconnect.  On reconnect the unread count is refetched to
 * catch anything missed while disconnected.
 */
const useSignalRNotifications = () => {
  const dispatch = useDispatch<AppDispatch>();
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const user = useSelector((state: RootState) => state.auth.user);

  // Derive EM base URL from VITE_FILE_BASE_URL (https://api.abilityglobal.io)
  const baseUrl = (
    (import.meta.env.VITE_AUTH_BASE_URL as string) ?? window.location.origin
  ).replace(/\/+$/, '');
  const hubUrl = `${baseUrl}/notifications`;

  const getToken = useCallback(() => localStorage.getItem('token') || '', []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: getToken,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    // ── ReceiveNotification ──────────────────────────────────────
    // The EM consumer now pushes the full notification DTO (id / type / title /
    // message / createdAt / isRead). We accept that object directly so the item
    // renders with its real server id and category — no REST refetch needed.
    // A plain string is still handled for backward-compat during rollout.
    connection.on('ReceiveNotification', (raw: any) => {
      // SignalR may serialize the DTO camelCase or PascalCase depending on the
      // server's hub-protocol options — read both so the rich payload always
      // resolves. A plain string is still handled for backward-compat.
      const payload = raw && typeof raw === 'object'
        ? {
            notificationId: raw.notificationId ?? raw.NotificationId,
            employeeId: raw.employeeId ?? raw.EmployeeId,
            userId: raw.userId ?? raw.UserId,
            message: raw.message ?? raw.Message,
            type: raw.type ?? raw.Type,
            title: raw.title ?? raw.Title,
            isRead: raw.isRead ?? raw.IsRead,
            createdAt: raw.createdAt ?? raw.CreatedAt,
          }
        : raw;
      const isObject = payload && typeof payload === 'object';
      const message: string = isObject ? (payload.message ?? '') : String(payload ?? '');

      // Show a toast for the incoming notification
      toast(message, {
        icon: '🔔',
        duration: 5000,
        style: { borderLeft: '4px solid #00BAA7' },
      });

      const normalized = {
        notificationId: isObject && payload.notificationId ? payload.notificationId : Date.now(),
        employeeId: isObject && payload.employeeId != null ? payload.employeeId : (user?.id ?? 0),
        userId: (isObject && payload.userId != null ? payload.userId : user?.id)?.toString() ?? '0',
        message,
        isRead: isObject ? !!payload.isRead : false,
        createdAt: isObject && payload.createdAt ? payload.createdAt : new Date().toISOString(),
        title: (isObject && payload.title) || 'Notification',
        type: (isObject && payload.type) || 'reminder',
        priority: 'medium' as const,
      };

      dispatch(prependNotification(normalized));

      if (isObject && payload.notificationId) {
        // Rich payload carried a real server id — the item is authoritative, so
        // only reconcile the badge count; no need to refetch the whole first page.
        dispatch(fetchUnreadCount());
      } else {
        // Legacy string payload (no id) — refetch so the item gets its real id.
        dispatch(fetchUnreadCount());
        dispatch(fetchNotifications({ pageNo: 1, pageSize: 10 }));
      }
    });

    // ── NotificationRead ─────────────────────────────────────────
    connection.on('NotificationRead', (notificationId: number) => {
      dispatch(markOneReadLocal(notificationId));
    });

    // ── AllNotificationsRead ─────────────────────────────────────
    connection.on('AllNotificationsRead', (_userId: number) => {
      dispatch(markAllReadLocal());
    });

    // ── Reconnect handler ────────────────────────────────────────
    connection.onreconnected(() => {
      if (import.meta.env.DEV) console.log('SignalR reconnected — refetching unread count');
      dispatch(fetchUnreadCount());
    });

    connection.onclose((err) => {
      if (err) console.warn('SignalR /notifications closed with error:', err);
    });

    // ── Start ────────────────────────────────────────────────────
    let isMounted = true;

    connection
      .start()
      .then(() => {
        if (!isMounted) {
          // Component unmounted while negotiating (React StrictMode double-mount) — clean up
          connection.stop().catch(() => { });
          return;
        }
        if (import.meta.env.DEV) console.log('✅ SignalR connected:', hubUrl);
      })
      .catch((err) => {
        if (isMounted) console.error('❌ SignalR /notifications error:', err);
      });

    return () => {
      isMounted = false;
      connectionRef.current?.stop().catch(() => { });
      connectionRef.current = null;
    };
  }, [isAuthenticated, hubUrl, dispatch, getToken, user?.id]);
};

export default useSignalRNotifications;
